import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Search, ClipboardList } from "lucide-react";
import { api } from "@/lib/api";
import { ORDER_TYPE, MARKET, relDay, fmtHours } from "@/lib/format";
import { OrderStatus, Priority } from "@/components/drift/StatusBadge";
import { PageHeader, EmptyState, LoadingRows, Avatar, Segmented, TableShell, Th, trCls } from "@/components/drift/common";
import { CompanyMark } from "@/components/drift/CompanyMark";
import { NewOrderDialog, useMontorar } from "@/components/drift/Dialogs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TABS = [["alle", "Alle"], ["ny", "Nye"], ["planlagt", "Planlagde"], ["paagaar", "Pågår"], ["ferdig", "Ferdige"], ["fakturert", "Fakturerte"]];
const ALL = "__all__";

export default function Orders() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const status = sp.get("status") || "alle";
  const assigned = sp.get("montor") || ALL;
  const market = sp.get("market") || "alle";
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(() => sp.get("new") === "1");
  const montorar = useMontorar();

  // Opened from the command menu or the dashboard with ?new=1: show the dialog and drop the flag.
  useEffect(() => {
    if (sp.get("new") === "1") {
      setOpen(true);
      const n = new URLSearchParams(sp);
      n.delete("new");
      setSp(n, { replace: true });
    }
  }, [sp, setSp]);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (status !== "alle") p.set("status", status);
    if (assigned !== ALL) p.set("assigned_to", assigned);
    if (market !== "alle") p.set("market", market);
    if (q) p.set("q", q);
    return api.get(`/drift/orders?${p.toString()}`).then((r) => setData(r.data)).catch(() => setData({ items: [], counts: {} }));
  }, [status, assigned, market, q]);

  useEffect(() => { const t = setTimeout(load, q ? 250 : 0); return () => clearTimeout(t); }, [load, q]);

  const update = (k, v) => { const n = new URLSearchParams(sp); if (!v || v === "alle" || v === ALL) n.delete(k); else n.set(k, v); setSp(n); };
  const counts = data?.counts || {};
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  const tabCount = (k) => (k === "alle" ? total : k.includes(",") ? k.split(",").reduce((s, x) => s + (counts[x] || 0), 0) : counts[k] || 0);
  const tabValue = TABS.some(([k]) => k === status) ? status : "alle";

  return (
    <div data-testid="orders-page">
      <PageHeader eyebrow="Operativt" title="Oppdrag" subtitle="Alle oppdrag – frå serviceoppdrag til prosjekt."
        actions={<Button onClick={() => setOpen(true)} className="h-9 rounded-lg bg-brand text-brand-foreground hover:bg-brand-dark" data-testid="orders-new-button"><Plus className="mr-1.5 h-4 w-4" strokeWidth={2} /> Nytt oppdrag</Button>} />

      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between" data-testid="drift-table-filters">
        <div data-testid="orders-status-tabs">
          <Segmented options={TABS.map(([k, l]) => [k, l, data ? tabCount(k) : undefined])} value={tabValue} onChange={(k) => update("status", k)} testid="orders-tab" />
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" strokeWidth={1.75} aria-hidden />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk i oppdrag …" className="h-9 w-56 rounded-lg bg-card pl-9" data-testid="orders-search-input" aria-label="Søk i oppdrag" />
          </div>
          <Select value={assigned} onValueChange={(v) => update("montor", v)}>
            <SelectTrigger className="h-9 w-44 rounded-lg bg-card" data-testid="orders-montor-filter"><SelectValue placeholder="Alle montørar" /></SelectTrigger>
            <SelectContent className="rounded-xl"><SelectItem value={ALL}>Alle montørar</SelectItem>{montorar.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={market} onValueChange={(v) => update("market", v)}>
            <SelectTrigger className="h-9 w-44 rounded-lg bg-card" data-testid="orders-market-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-xl"><SelectItem value="alle">Alle marknader</SelectItem>{Object.entries(MARKET).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {!data ? <LoadingRows n={8} header /> : data.items.length === 0 ? (
        <EmptyState icon={ClipboardList} title="Ingen oppdrag" text="Prøv eit anna filter, eller opprett eit nytt oppdrag." testid="orders-empty" action={<Button variant="outline" size="sm" className="rounded-lg bg-card" onClick={() => setOpen(true)}>Nytt oppdrag</Button>} />
      ) : (
        <TableShell testid="drift-data-table">
          <thead className="border-b border-border/70">
            <tr>
              <Th className="hidden md:table-cell">Ref</Th>
              <Th>Oppdrag</Th>
              <Th className="hidden lg:table-cell">Type</Th>
              <Th className="hidden sm:table-cell">Montør</Th>
              <Th>Når</Th>
              <Th className="hidden lg:table-cell" align="right">Timar</Th>
              <Th>Status</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/60">
            {data.items.map((o) => (
              <tr key={o.id} onClick={() => navigate(`/drift/oppdrag/${o.id}`)} onKeyDown={(e) => { if (e.key === "Enter") navigate(`/drift/oppdrag/${o.id}`); }} tabIndex={0} className={trCls} data-testid="order-row">
                <td className="hidden px-4 py-3 font-mono text-[12.5px] tabular text-muted-foreground md:table-cell">{o.ref}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <CompanyMark name={o.customer_name} brand={o.customer_brand} type={o.customer_type} size="md" className="hidden sm:inline-flex" />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2"><span className="font-medium">{o.title}</span><Priority priority={o.priority} hideNormal /></div>
                      <p className="truncate text-[12.5px] text-muted-foreground">{o.customer_name} · {o.location_address || o.location_name}</p>
                    </div>
                  </div>
                </td>
                <td className="hidden px-4 py-3 text-muted-foreground lg:table-cell">{ORDER_TYPE[o.type]}</td>
                <td className="hidden px-4 py-3 sm:table-cell">{o.assigned_name ? <span className="inline-flex items-center gap-2"><Avatar name={o.assigned_name} initials={o.assigned_initials} size="sm" tone="soft" /><span className="text-muted-foreground">{o.assigned_name}</span></span> : <span className="inline-flex items-center gap-1.5 text-[12.5px] text-warn"><span className="h-1.5 w-1.5 rounded-full bg-warn-dot" aria-hidden />Ikkje tildelt</span>}</td>
                <td className="px-4 py-3 text-muted-foreground"><span className="whitespace-nowrap">{relDay(o.scheduled_date)}</span>{o.scheduled_time && <span className="ml-1.5 font-mono text-[12px] tabular">{o.scheduled_time}</span>}</td>
                <td className="hidden px-4 py-3 text-right font-mono text-[12.5px] tabular text-muted-foreground lg:table-cell">{o.totals?.hours ? fmtHours(o.totals.hours) : "–"}{o.budget_hours ? <span className="text-[11.5px]"> / {o.budget_hours}</span> : null}</td>
                <td className="px-4 py-3"><OrderStatus status={o.status} /></td>
              </tr>
            ))}
          </tbody>
        </TableShell>
      )}
      <NewOrderDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
